<script setup>
  import { computed } from 'vue';
  import { useWindAlertStore } from '@/stores/WindAlertStore';
  import { useUserStore } from '@/stores/UserStore';
  import Toggle from '@/components/controls/Toggle.vue';
  import Popup from '@/components/controls/Popup.vue';
  import WindDirectionPie from '@/components/controls/WindDirectionPie.vue';
  import Icon from '@/components/icons/Icon.vue';
  import windInfo from '@/data/windInfo.json';

  const windAlertStore = useWindAlertStore();
  const userStore = useUserStore();

  const props = defineProps({
    alert: {
      type: Object,
      required: true,
    },
    isToggling: {
      type: Boolean,
      default: false,
    },
  });

  const emits = defineEmits(['edit', 'delete', 'toggle']);

  const windDirectionOrder = windInfo.cardinalDirections;

  const hasForecast = computed(() => props.alert.model_ids && props.alert.model_ids.length > 0);
  const hasObservations = computed(() => props.alert.frequency != null && props.alert.frequency !== '');
  const isGustTrigger = computed(() => String(props.alert.trigger || '').toLowerCase() === 'gust');

  const alertType = computed(() => {
    if (hasForecast.value && hasObservations.value) return 'Observations & Forecast';
    if (hasForecast.value) return 'Forecast';
    return 'Observations';
  });

  const windThreshold = computed(() => {
    const units = userStore.units?.wind || 'MPH';
    const unitsLabel = units.toUpperCase();
    const min = props.alert.min_threshold || 0;
    const max = props.alert.max_threshold;

    if (max) return `${min} to ${max} ${unitsLabel}`;
    return `${min} to Any ${unitsLabel}`;
  });

  const selectedDirections = computed(() => {
    const raw = props.alert.wind_directions ?? props.alert.directions;
    let dirs = [];
    if (Array.isArray(raw)) dirs = raw;
    else if (typeof raw === 'string') dirs = raw.split(',').map(v => v.trim()).filter(Boolean);

    return dirs
      .map(d => {
        const n = typeof d === 'number' ? d : parseInt(d, 10);
        return Number.isNaN(n) ? d : windDirectionOrder[n];
      })
      .filter(name => windDirectionOrder.includes(name));
  });

  const timeframeLabel = computed(() => {
    if (isGustTrigger.value) return 'N/A';
    if (!hasObservations.value) return '';

    const freq = parseInt(props.alert.frequency);

    if (freq === 30) return 'Every 30 Minutes';
    if (freq === 60) return 'Every Hour';
    if (freq === 120) return 'Every 2 Hours';
    if (freq === 300) return 'Every 5 Hours';
    if (freq === 720) return 'Every 12 Hours';
    if (freq === 1440) return 'Once a Day';
    return `Every ${freq} Minutes`;
  });

  const alertDeviceInfo = computed(() => {
    if (!props.alert.alert_devices) return [];

    return props.alert.alert_devices
      .map(deviceId => {
        const device = windAlertStore.getAlertDevice(deviceId);
        if (!device) return null;
        return { ...device };
      })
      .filter(Boolean);
  });

  function isDeviceEnabledForAlert(deviceType) {
    return alertDeviceInfo.value.find(d => d.type === deviceType) !== undefined;
  }

  function isInactiveDeviceForAlert(deviceType) {
    const dev = alertDeviceInfo.value.find(d => d.type === deviceType);
    return !!dev && dev.enabled === false;
  }

  function isDeviceTypeConfigured(deviceType) {
    return (windAlertStore.alertDevices || []).some(d => d.type === deviceType);
  }

  function deviceDotState(deviceType) {
    if (isDeviceEnabledForAlert(deviceType)) {
      return isInactiveDeviceForAlert(deviceType) ? 'warning' : 'active';
    }
    if (isDeviceTypeConfigured(deviceType)) return 'unselected';
    return null;
  }
</script>

<template>
  <div class="jwx-alert-row" :data-active="alert.enabled">
    <div class="jwx-alert-row__type">
      <div class="jwx-alert-row__alert-icon-wrap" :data-active="alert.enabled">
        <Icon name="bell-solid" class="jwx-alert-row__alert-icon" />
      </div>
      <span class="jwx-alert-row__type-label">{{ alertType }}</span>
    </div>

    <div class="jwx-alert-row__details">
      <WindDirectionPie :directions="selectedDirections" />
      <div class="jwx-alert-row__detail">
        <span class="jwx-alert-row__detail-label">Wind Threshold</span>
        <span class="jwx-alert-row__detail-value">{{ windThreshold }}</span>
      </div>
      <div class="jwx-alert-row__detail">
        <span class="jwx-alert-row__detail-label">Trigger</span>
        <span class="jwx-alert-row__detail-value">{{ alert.trigger || 'Average' }}</span>
      </div>
      <div class="jwx-alert-row__detail">
        <span class="jwx-alert-row__detail-label">Timeframe</span>
        <span
          class="jwx-alert-row__detail-value"
          :class="{ 'jwx-alert-row__detail-value--muted': isGustTrigger }"
        >{{ timeframeLabel }}</span>
      </div>
      <div class="jwx-alert-row__devices">
        <span
          v-for="(device, type) in windAlertStore.deviceTypeMap"
          :key="device.id"
          class="jwx-alert-row__device"
          :data-active="isDeviceEnabledForAlert(type)"
          :title="type"
        >
          <span class="jwx-alert-row__device-icon">
            <Icon :name="device.icon" />
          </span>
          <span
            v-if="deviceDotState(type)"
            class="jwx-alert-row__device-dot"
            :data-state="deviceDotState(type)"
          ></span>
        </span>
      </div>
    </div>
    <div class="jwx-alert-row__actions">
      <Toggle
        :id="`alert-toggle-${alert.id}`"
        v-model="alert.enabled"
        :hideLabelText="true"
        :disabled="isToggling"
        size="small"
        data-background="surface"
        @input:change="emits('toggle', alert)"
      >
        Toggle Alert
      </Toggle>

      <Popup
        :id="`alert-menu-${alert.id}`"
        positionY="bottom"
        positionX="right"
      >
        <template #button-content>
          <div class="jwx-popup__button">
            <Icon name="more-menu" class="jwx-alert-row__menu-icon" />
            <span class="sr-only">Alert Actions</span>
          </div>
        </template>
        <template #menu-content>
          <div class="jwx-popup-menu">
            <div class="jwx-action-menu">
              <ul class="jwx-action-menu__list">
                <li class="jwx-action-menu__action">
                  <button
                    type="button"
                    class="jwx-action-menu__action-button"
                    @click="emits('edit', alert)"
                  >
                    <Icon name="gear" class="jwx-action-menu__action-icon" />
                    <span class="jwx-action-menu__action-label">Alert Settings</span>
                  </button>
                </li>
                <li class="jwx-action-menu__action">
                  <button
                    type="button"
                    @click.prevent="emits('delete', alert)"
                    class="jwx-action-menu__action-button"
                  >
                    <Icon name="trash" class="jwx-action-menu__action-icon" />
                    <span class="jwx-action-menu__action-label">Delete Alert</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </Popup>
    </div>
  </div>
</template>
